import React, { Suspense } from 'react';
import { RouteComponentProps } from 'react-router';
import { Switch } from 'react-router-dom';
import { ApiUrls } from '@api/apiUrls';
import { IApiMTradingAccount, IApiTradingPlatformType } from '@api/interfaces/data-contracts';
import AccountArchivingForm from '@components-member/TradingAccounts/AccountArchiving';
import { AccountDetailsNotifications } from '@components-member/TradingAccounts/AccountDetails/AccountDetailsNotifications';
import {
  InvestorPasswordForm,
  LeverageForm,
  MasterPasswordForm,
  RefillForm,
} from '@components-member/TradingAccounts/AccountDetails/formFields';
import TradingAccountHistory from '@components-member/TradingAccounts/AccountHistory/AccountHistory';
import { InvestorSettingsForm } from '@components-member/TradingAccounts/CopyTradingAccounts/InvestorSettingsForm';
import { MasterSettingsForm } from '@components-member/TradingAccounts/CopyTradingAccounts/MasterSettingsForm';
import MamInvestorTerminateLPOA from '@components-member/mam/mamInvestorTerminateLPOA';
import { DepositAsync, TransferAsync, WithdrawalAsync } from '@components-member/psp/asyncPspComponents';
import { Page404 } from '@components-shared/404/404';
import RouteWithPageTitle from '@components-shared/RouteWithPageTitle';
import Preloader from '@components-shared/preloader';
import { keyMap } from '@locales/lokalise/keyMap';
import { getAccountCategoryType } from '@utils/getAccountCategoryType';
import { isOnboardingRestricted, ROUTE_CATEGORY } from '@utils/isOnboardingRestricted';
import { IOnboardingConfiguration, IUser } from 'global.interfaces';
import { observer } from 'mobx-react';
import { useMobxStore } from 'store/mobx';

export interface IAccountDetailsTabsPanelProps {
  rootPath: string;
  accountId: string;
  account: IApiMTradingAccount;
  userInfo: IUser;
  onboardingConfiguration: IOnboardingConfiguration;
  onConfirm: () => Promise<void>;
}

const AccountDetailsTabsPanel = observer((props: IAccountDetailsTabsPanelProps): JSX.Element | null => {
  const { rootPath, accountId, account, userInfo, onboardingConfiguration, onConfirm } = props;

  const { isMasterAccount, isInvestorAccount, isInMamInvestorGroupMappingType } = getAccountCategoryType({
    groupMappingType: account.groupMappingType,
    investorId: account.investorId || null,
    masterId: account.masterId || null,
  });

  const {
    accountStore: {
      generalSettings: { lpoaEnabledForMamAccounts },
    },
    featuresStore,
  } = useMobxStore();

  if (!account) {
    return null;
  }

  const { groupMappingType } = account;

  const extendedActionsForMamEnabled = isInMamInvestorGroupMappingType && lpoaEnabledForMamAccounts;
  const isLPOACancelRouteAllowed = extendedActionsForMamEnabled && account.isMamInvestor;

  const isSocialTradingRouteRestricted = isOnboardingRestricted(
    onboardingConfiguration,
    userInfo,
    ROUTE_CATEGORY.SOCIAL_TRADING,
  );

  const isInvestorSettingsRouteRestricted = !isInvestorAccount || isSocialTradingRouteRestricted;
  const isMasterSettingsRouteRestricted = !isMasterAccount || isSocialTradingRouteRestricted;
  const isNotificationsAllowed = account.platformType === IApiTradingPlatformType.Gmi;

  return (
    <div className="tab-panel">
      <>
        <Suspense fallback={<Preloader />}>
          <Switch>
            <RouteWithPageTitle
              pageTitle="Trading account master password"
              path={`${rootPath}/masterpassword`}
              exact
              render={(props: RouteComponentProps) => (
                <MasterPasswordForm
                  accountId={accountId}
                  title={keyMap.tradingAccountsActionsSetPasswordTitle}
                  api={ApiUrls.tradingAccountUser.changeMasterPassword}
                  {...props}
                />
              )}
            />
            <RouteWithPageTitle
              pageTitle="Trading account investor password"
              path={`${rootPath}/investorpassword`}
              exact
              render={(props: RouteComponentProps) => (
                <InvestorPasswordForm
                  accountId={accountId}
                  title={keyMap.tradingAccountsActionsSetPasswordTitle}
                  api={ApiUrls.tradingAccountUser.changeInvestorPassword}
                  {...props}
                />
              )}
            />
            <RouteWithPageTitle
              pageTitle="Trading account leverage"
              path={`${rootPath}/leverage`}
              exact
              render={(props: RouteComponentProps) => (
                <LeverageForm
                  accountId={accountId}
                  title={keyMap.tradingAccountsActionsSaveTitle}
                  api={ApiUrls.tradingAccountUser.changeLeverage}
                  account={account}
                  {...props}
                />
              )}
            />

            {account.investorId && (
              <RouteWithPageTitle
                pageTitle="Follower copy trading account settings"
                path={`${rootPath}/follower`}
                exact
                render={(props: RouteComponentProps) => (
                  <InvestorSettingsForm investorId={account.investorId || null} {...props} />
                )}
                routeIsNotAllowed={isInvestorSettingsRouteRestricted}
              />
            )}

            {account.masterId && (
              <RouteWithPageTitle
                pageTitle="Master copy trading account settings"
                path={`${rootPath}/master`}
                exact
                render={(props: RouteComponentProps) => (
                  <MasterSettingsForm
                    masterId={account.masterId || null}
                    groupMappingType={groupMappingType}
                    {...props}
                  />
                )}
                routeIsNotAllowed={isMasterSettingsRouteRestricted}
              />
            )}

            <RouteWithPageTitle
              pageTitle="Trading account transfer"
              path={`${rootPath}/transfer`}
              exact
              render={(props) => <TransferAsync accountId={accountId} {...props} />}
            />

            <RouteWithPageTitle
              pageTitle="Trading account refill"
              path={`${rootPath}/refill`}
              exact
              render={(props: RouteComponentProps) => (
                <RefillForm
                  accountId={accountId}
                  title={keyMap.tradingAccountsRefillFormRefillActionTitle}
                  api={ApiUrls.tradingAccountUser.refill}
                  {...props}
                />
              )}
            />
            <RouteWithPageTitle
              pageTitle="Trading account history"
              path={`${rootPath}/history`}
              exact
              render={(props: RouteComponentProps) => (
                <TradingAccountHistory accountId={accountId} account={account} {...props} />
              )}
            />

            <RouteWithPageTitle
              pageTitle="Trading account deposit"
              path={`${rootPath}/deposit`}
              exact
              render={(props) => <DepositAsync accountId={accountId} {...props} />}
            />

            <RouteWithPageTitle
              pageTitle="Trading account withdraw"
              path={`${rootPath}/withdraw`}
              exact
              render={(props) => <WithdrawalAsync accountId={accountId} {...props} />}
            />

            {featuresStore.featureFlags.MAM && (
              <RouteWithPageTitle
                pageTitle="Terminate LPOA"
                path={`${rootPath}/terminateLPOA`}
                exact
                render={(props) => <MamInvestorTerminateLPOA accountId={accountId as unknown as number} {...props} />}
                routeIsNotAllowed={!isLPOACancelRouteAllowed}
              />
            )}
            <RouteWithPageTitle
              pageTitle="Trading account archiving"
              path={`${rootPath}/archive`}
              exact
              render={(props: RouteComponentProps) => <AccountArchivingForm accountId={accountId} {...props} />}
            />

            <RouteWithPageTitle
              pageTitle="Trading account notifications"
              path={`${rootPath}/notifications`}
              exact
              render={() => <AccountDetailsNotifications account={account} onConfirm={onConfirm} />}
              routeIsNotAllowed={!isNotificationsAllowed}
            />

            <RouteWithPageTitle pageTitle="Page not found" render={() => <Page404 redirectTo="/member" />} />
          </Switch>
        </Suspense>
      </>
    </div>
  );
});

export default AccountDetailsTabsPanel;
